import {
  TypeImplementation,
  withSystem,
  withMeta,
  Expressions,
  Type,
  EnvTypes,
} from '@packages/systems/wfdl';
import {
  functionOf,
  TText,
  TListOfElement,
  ECall,
  TTypeConstructor,
  TGeneric,
  TRowCons,
  TEmptyRecord,
  TTypeReference,
} from '@packages/systems/wfdl-ast';
import {
  qualify,
  typeName,
  iconName,
  pluginName as qualifiedPluginName,
} from '@packages/systems/wfdl-Qualified';
import {pluginName} from '../constants';
import {type DynamoEnvValueType} from '../types';
import {transformers} from '@packages/systems/dynamo/utils/Transformers';
import {
  DATETIME_FORMAT_OPTIONS,
  DATE_FORMAT_OPTIONS,
} from '@packages/systems/dynamo/constants';
import {TOption} from '@packages/systems/plugins/Option';
import {
  TPredicate,
  BooleanTag,
  FALSE,
  TRUE,
} from '@packages/systems/plugins-utils/Boolean';
import {operand} from '@packages/systems/plugins/ConditionsOperand';
import moment from 'moment-timezone';
import {
  TDynamoDate,
  TDynamoDateTime,
} from '@packages/systems/plugins-utils/DynamoGateway';
import {parseDate} from '@packages/systems/dynamo/utils/DynamoConditionUtils';
import {TDate} from '@packages/systems/plugins/DateTime';
import {tyfun} from '@packages/utilities/wfdl-helpers';
import {TDateFormat} from './DateFormat';

export {DateFormatType, TDateFormat} from './DateFormat';

const TDateCondition = TTypeConstructor(
  qualify(qualifiedPluginName('DateCondition'), typeName('DateCondition'))
);

export const DateType = TypeImplementation.Custom({
  render: () => null,
  icon: TypeImplementation.IconByName(
    qualify(pluginName, iconName('DateTime'))
  ),
});

export const DateTimeType = TypeImplementation.Custom({
  render: () => null,
  icon: TypeImplementation.IconByName(
    qualify(pluginName, iconName('DateTime'))
  ),
  system: {
    displayName: 'Date Time',
  },
});

export const dynamoDateToText = createToTextTransformer('date');
export const dynamoDateToTextWithTimezone =
  createToTextTransformerWithTimezone('date');

export const dynamoDateToListOfElements =
  createToListOfElementsTransformer('date');
export const dynamoDateToListOfElementsWithTimezone =
  createToListOfElementsTransformerWithTimezone('date');

export const dynamoDateTimeToText = createToTextTransformer('date-time');
export const dynamoDateTimeToTextWithTimezone =
  createToTextTransformerWithTimezone('date-time');

export const dynamoDateTimeToListOfElements =
  createToListOfElementsTransformer('date-time');
export const dynamoDateTimeToListOfElementsWithTimezone =
  createToListOfElementsTransformerWithTimezone('date-time');

export const dynamoDateToDate = createToDateTransformer('date');
export const dynamoDateTimeToDate = createToDateTransformer('date-time');

export const dynamoDateToDateWithTimezone =
  createToDateTransformerWithTimezone('date');
export const dynamoDateTimeToDateWithTimezone =
  createToDateTransformerWithTimezone('date-time');

export const dynamoDateOperand = operand(TOption(TDynamoDate));

export const dynamoDateTimeOperand = operand(TOption(TDynamoDateTime));

export const dynamoDateIsSet = isSetCreator(TDynamoDate);
export const dynamoDateIsNotSet = isNotSetCreator(TDynamoDate);

export const dynamoDateTimeIsSet = isSetCreator(TDynamoDateTime);
export const dynamoDateTimeIsNotSet = isNotSetCreator(TDynamoDateTime);

type FormatVariant = 'date' | 'date-time';

function createToListOfElementsTransformer(
  variant: FormatVariant
): EnvTypes.TypedValue {
  return {
    envType: {
      dataType: createFunctionType(variant, TListOfElement),
      exported: 'visible',
      implicit: 'prefilledArgs',
    },
    expression: ECall({
      fun: Expressions.qualifiedVariable(
        'DynamoGateway',
        variant === 'date'
          ? 'dynamoDateToListOfElementsWithTimezone'
          : 'dynamoDateTimeToListOfElementsWithTimezone'
      ),
      arg: Expressions.qualifiedVariable('__SitePlugin', 'timezone'),
    }),
  };
}

export function createToListOfElementsTransformerWithTimezone(
  variant: FormatVariant
): EnvTypes.TypedValue {
  return {
    envType: {
      dataType: createFunctionType(variant, TListOfElement, [TText]),
      exported: 'no',
      implicit: 'prefilledArgs',
    },
    value:
      (timezone: string) =>
      (format: string) =>
      (x: DynamoEnvValueType<string>) => {
        const formattedDate = formatDate(x, format, timezone);
        return typeof formattedDate === 'string'
          ? [
              {
                text: true,
                type: null,
                id: 'temp',
                children: [],
                data: {value: formattedDate},
              },
            ]
          : [];
      },
  };
}

function createToTextTransformer(variant: FormatVariant): EnvTypes.TypedValue {
  return {
    envType: {
      dataType: createFunctionType(variant, TText),
      exported: 'visible',
      implicit: 'prefilledArgs',
    },
    expression: ECall({
      fun: Expressions.qualifiedVariable(
        'DynamoGateway',
        variant === 'date'
          ? 'dynamoDateToTextWithTimezone'
          : 'dynamoDateTimeToTextWithTimezone'
      ),
      arg: Expressions.qualifiedVariable('__SitePlugin', 'timezone'),
    }),
  };
}

function createToTextTransformerWithTimezone(
  formatVariant: FormatVariant
): EnvTypes.TypedValue {
  return {
    envType: {
      dataType: createFunctionType(formatVariant, TText, [TText]),
      exported: 'no',
      implicit: 'prefilledArgs',
    },
    value:
      (timezone: string) =>
      (format: string) =>
      (x: DynamoEnvValueType<string>) => {
        const formattedDate = formatDate(x, format, timezone);
        return typeof formattedDate === 'string' ? formattedDate : '';
      },
  };
}

function createToDateTransformer(variant: FormatVariant): EnvTypes.TypedValue {
  return {
    envType: {
      dataType: functionOf(
        withSystem({isBindable: true}, TOption(getDateVariantType(variant))),
        TDate
      ),
      exported: 'visible',
      implicit: 'prefilledArgs',
    },
    expression: ECall({
      fun: Expressions.qualifiedVariable(
        'DynamoGateway',
        variant === 'date'
          ? 'dynamoDateToDateWithTimezone'
          : 'dynamoDateTimeToDateWithTimezone'
      ),
      arg: Expressions.qualifiedVariable('__SitePlugin', 'timezone'),
    }),
  };
}

function createToDateTransformerWithTimezone(
  variant: FormatVariant
): EnvTypes.TypedValue {
  return {
    envType: {
      dataType: functionOf(
        TText,
        functionOf(
          withSystem({isBindable: true}, TOption(getDateVariantType(variant))),
          TDate
        )
      ),
      exported: 'no',
      implicit: 'prefilledArgs',
    },
    value: (timezone: string) => (x: DynamoEnvValueType<string>) =>
      x === null || x.tag === 'None'
        ? ''
        : moment.tz(x.val, timezone).format('YYYY-MM-DD'),
  };
}

function createFunctionType(
  formatVariant: FormatVariant,
  targetType: Type,
  leadingArgs: Type[] = []
) {
  const callableType = tyfun(
    leadingArgs,
    functionOf(
      createFormatArg(formatVariant),
      functionOf(
        withSystem(
          {isBindable: true},
          TOption(getDateVariantType(formatVariant))
        ),
        targetType
      )
    )
  );

  const label = 'Format';
  // Binding UI reads the outer label; the checker discards the generic wrapper,
  // so the callable body also needs the label after instantiation.
  return withSystem(
    {label},
    TGeneric({
      arg: 'R',
      body: withSystem({label}, callableType),
    })
  );
}

function createFormatArg(variant: FormatVariant) {
  const formatOptions = getDateFormatOptions(variant);
  return withMeta(
    {variant: 'menu', dateFormatVariant: variant},
    withSystem(
      {defaultValue: formatOptions[0], labelVariant: 'hidden'},
      TDateFormat(
        formatOptions.reduce(
          (row, format) =>
            TRowCons({
              label: format,
              type: withSystem({label: format}, TEmptyRecord),
              tail: row,
            }),
          TTypeReference('R')
        )
      )
    )
  );
}

function getDateFormatOptions(variant: FormatVariant): string[] {
  return variant === 'date-time'
    ? DATETIME_FORMAT_OPTIONS
    : DATE_FORMAT_OPTIONS;
}

function getDateVariantType(variant: string | FormatVariant) {
  return variant === 'date-time' ? TDynamoDateTime : TDynamoDate;
}

function formatDate(
  value: DynamoEnvValueType<string>,
  format: string,
  timezone: string
) {
  if (value === null || value.tag === 'None') {
    return null;
  }

  return transformers(
    value.val,
    {type: 'date', params: [format]},
    {timezone, collectionSlugMap: {}}
  );
}

function isSetCreator(type: Type): EnvTypes.TypedValue {
  return {
    envType: {
      dataType: withSystem({label: 'is set'}, TPredicate(TOption(type))),
      exported: 'visible',
    },
    value: (x: DynamoEnvValueType<unknown>) => {
      if (x === null || x.tag === 'None') {
        return FALSE;
      }

      return TRUE;
    },
  };
}

function isNotSetCreator(type: Type): EnvTypes.TypedValue {
  return {
    envType: {
      dataType: withSystem({label: 'is not set'}, TPredicate(TOption(type))),
      exported: 'visible',
    },
    value: (x: DynamoEnvValueType<unknown>) => {
      if (x === null || x.tag === 'None') {
        return TRUE;
      }

      return FALSE;
    },
  };
}

interface OperatorConfig {
  label: string;
  functionNameSuffix: string;
  compareFn: (date1: number, date2: number) => BooleanTag;
  legacyOperator?: string;
}

type DateValue =
  | {tag: 'Exact'; val: string}
  | {
      tag: 'Relative';
      val: string;
    }
  | {tag: 'Today'; val: Record<string, never>};

/**
 * Compares two dates using the provided compare function.
 * Normalizes both dates to the start of the day before comparing.
 */
function compareDates(
  dateCondition: DateValue,
  date: DynamoEnvValueType<unknown>,
  compareFn: (date2Time: number, date1: number) => BooleanTag,
  timezone: string,
  legacyOperator: string | undefined
): BooleanTag {
  if (date === null || date.tag === 'None' || typeof date.val !== 'string') {
    return FALSE;
  }

  if (dateCondition.tag === 'Exact') {
    const exactDate = dateCondition.val;
    if (exactDate === undefined || exactDate === null) {
      return FALSE;
    }
    const cmsDateFieldMoment = moment.tz(date.val, timezone).startOf('day');
    const conditionDate = moment
      .tz(exactDate, 'YYYY-MM-DD', timezone)
      .startOf('day')
      .valueOf();
    return compareFn(cmsDateFieldMoment.valueOf(), conditionDate);
  } else if (dateCondition.tag === 'Relative') {
    const relativeData = dateCondition.val;
    const parsedDate = parseDate({
      value: relativeData,
      timezone,
      operator: legacyOperator,
    });
    if (parsedDate === null || parsedDate === undefined) {
      return FALSE;
    }
    const cmsDateFieldMoment = moment.utc(date.val).tz(timezone).startOf('day');
    return compareFn(cmsDateFieldMoment.valueOf(), parsedDate.valueOf());
  } else if (dateCondition.tag === 'Today') {
    const cmsDateFieldMoment = moment.tz(date.val, timezone).startOf('day');
    const today = moment.tz(timezone).startOf('day');
    return compareFn(cmsDateFieldMoment.valueOf(), today.valueOf());
  }
  return FALSE;
}

/**
 * Creates operator transformers for both date and date-time variants.
 * Returns an object with transformer and transformerWithTimezone functions
 * that can be called with the variant type.
 */
function createDateAndDateTimeOperators(config: OperatorConfig) {
  const transformer = (variant: FormatVariant): EnvTypes.TypedValue => {
    return {
      envType: {
        dataType: withSystem(
          {label: config.label},
          functionOf(
            TDateCondition,
            TPredicate(TOption(getDateVariantType(variant)))
          )
        ),
        exported: 'visible',
      },
      expression: ECall({
        fun: Expressions.qualifiedVariable(
          'DynamoGateway',
          variant === 'date'
            ? `dynamoDate${config.functionNameSuffix}WithTimezone`
            : `dynamoDateTime${config.functionNameSuffix}WithTimezone`
        ),
        arg: Expressions.qualifiedVariable('__SitePlugin', 'timezone'),
      }),
    };
  };

  const transformerWithTimezone = (
    variant: FormatVariant
  ): EnvTypes.TypedValue => {
    return {
      envType: {
        dataType: functionOf(
          TText,
          functionOf(
            TDateCondition,
            TPredicate(TOption(getDateVariantType(variant)))
          )
        ),
        exported: 'no',
        implicit: 'prefilledArgs',
      },
      value:
        (timezone: string) =>
        (date1: DateValue) =>
        (date2: DynamoEnvValueType<unknown>) => {
          return compareDates(
            date1,
            date2,
            config.compareFn,
            timezone,
            config.legacyOperator
          );
        },
    };
  };

  return {
    date: transformer('date'),
    dateWithTimezone: transformerWithTimezone('date'),
    dateTime: transformer('date-time'),
    dateTimeWithTimezone: transformerWithTimezone('date-time'),
  };
}

export const {
  date: dynamoDateIs,
  dateWithTimezone: dynamoDateIsWithTimezone,
  dateTime: dynamoDateTimeIs,
  dateTimeWithTimezone: dynamoDateTimeIsWithTimezone,
} = createDateAndDateTimeOperators({
  label: 'is',
  functionNameSuffix: 'Is',
  compareFn: (d1, d2) => {
    if (d1 === d2) {
      return TRUE;
    }
    return FALSE;
  },
});
export const {
  date: dynamoDateIsNot,
  dateWithTimezone: dynamoDateIsNotWithTimezone,
  dateTime: dynamoDateTimeIsNot,
  dateTimeWithTimezone: dynamoDateTimeIsNotWithTimezone,
} = createDateAndDateTimeOperators({
  label: 'is not',
  functionNameSuffix: 'IsNot',
  compareFn: (d1, d2) => {
    if (d1 !== d2) {
      return TRUE;
    }
    return FALSE;
  },
});
export const {
  date: dynamoDateIsBefore,
  dateWithTimezone: dynamoDateIsBeforeWithTimezone,
  dateTime: dynamoDateTimeIsBefore,
  dateTimeWithTimezone: dynamoDateTimeIsBeforeWithTimezone,
} = createDateAndDateTimeOperators({
  label: 'is before',
  functionNameSuffix: 'IsBefore',
  compareFn: (d1, d2) => {
    if (d1 < d2) {
      return TRUE;
    }
    return FALSE;
  },
});
export const {
  date: dynamoDateIsOnOrBefore,
  dateWithTimezone: dynamoDateIsOnOrBeforeWithTimezone,
  dateTime: dynamoDateTimeIsOnOrBefore,
  dateTimeWithTimezone: dynamoDateTimeIsOnOrBeforeWithTimezone,
} = createDateAndDateTimeOperators({
  label: 'is on or before',
  functionNameSuffix: 'IsOnOrBefore',
  compareFn: (d1, d2) => {
    if (d1 <= d2) {
      return TRUE;
    }
    return FALSE;
  },
  legacyOperator: 'lte',
});
export const {
  date: dynamoDateIsAfter,
  dateWithTimezone: dynamoDateIsAfterWithTimezone,
  dateTime: dynamoDateTimeIsAfter,
  dateTimeWithTimezone: dynamoDateTimeIsAfterWithTimezone,
} = createDateAndDateTimeOperators({
  label: 'is after',
  functionNameSuffix: 'IsAfter',
  compareFn: (d1, d2) => {
    if (d1 > d2) {
      return TRUE;
    }
    return FALSE;
  },
});

export const {
  date: dynamoDateIsOnOrAfter,
  dateWithTimezone: dynamoDateIsOnOrAfterWithTimezone,
  dateTime: dynamoDateTimeIsOnOrAfter,
  dateTimeWithTimezone: dynamoDateTimeIsOnOrAfterWithTimezone,
} = createDateAndDateTimeOperators({
  label: 'is on or after',
  functionNameSuffix: 'IsOnOrAfter',
  compareFn: (d1, d2) => {
    if (d1 >= d2) {
      return TRUE;
    }
    return FALSE;
  },
  legacyOperator: 'gte',
});

const forTesting = {
  compareDates,
};

export {forTesting};
